import { useContext, useEffect, useState } from "react";
import ProjectInfo from "../../models/projectInfo";
import TableColum from "../../models/tableColumn";
import MapLayer from "../../models/mapLayer";
import ModalWindow from "../common/overlays/ModalWindow";
import ValidatorManager from "../../components/common/validationManager";
import Validator from "../common/Validator";
import User from "../../models/user";
import UserContext from "../../contexts/userContext";
import MapLayerShareIcon from "./MapLayerShareIcon";
import ThematicMapLayerEdit from "./thematic-maps/ThematicMapLayerEdit";
import DropdownOption from "../common/DropdownOption";
import MapLayerType from "../../models/mapLayerType";
import Dropdown from "../common/Dropdown";
import mapLayerTypeHelper from "../../models/mapLayerTypeHelper";
import GeospatialMapLayerEdit from "./GeospatialMapLayerEdit";
import ThematicMapLayer from "../../models/thematicMapLayer";
import GeospatialMapLayer from "../../models/geospatialMapLayer";

const validator = {
  layerName: "layerName",
};

const MapLayerEdit: React.FC<{
  project: ProjectInfo;
  tableColumns: TableColum[];
  mapLayer: MapLayer;
  isNew: boolean;
  onCreateMapLayer: any;
  onUpdateMapLayer: any;
  onDeleteMapLayer: any;
  onCancel: any;
}> = ({
  project,
  tableColumns,
  mapLayer: inputMapLayer,
  isNew,
  onCreateMapLayer,
  onUpdateMapLayer,
  onDeleteMapLayer,
  onCancel,
}) => {
  const [showDeleteWindow, setShowDeleteWindow] = useState<boolean>(false);

  const [showConfirmIsShared, setShowConfirmIsShared] =
    useState<Boolean>(false);

  const [showConfirmIsDefault, setShowConfirmIsDefault] =
    useState<Boolean>(false);

  const [showConfirmSave, setShowConfirmSave] = useState<Boolean>(false);

  const user = useContext<User | undefined>(UserContext);

  const [validatorManager] = useState<ValidatorManager>(new ValidatorManager());

  const [errorMessage, setErrorMessage] = useState<string>("");

  const [mapLayer, setMapLayer] = useState<MapLayer>(new MapLayer());

  const [mapLayerTypeOptions, setMapLayerTypeOptions] = useState<
    DropdownOption<string>[]
  >([]);

  useEffect(() => {
    let mapLayerTypeOptions: DropdownOption<MapLayerType>[] = [
      {
        value: MapLayerType.ThematicLayer,
        label: mapLayerTypeHelper.getMapLayerTypeDisplayName(
          MapLayerType.ThematicLayer,
        ),
      },
      {
        value: MapLayerType.GeospatialLayer,
        label: mapLayerTypeHelper.getMapLayerTypeDisplayName(
          MapLayerType.GeospatialLayer,
        ),
      },
    ];

    if (!project.hasThematicMaps) {
      mapLayerTypeOptions = mapLayerTypeOptions.filter(
        (o) => o.value !== MapLayerType.ThematicLayer,
      );
    }

    setMapLayerTypeOptions(mapLayerTypeOptions);

    inputMapLayer.selectedMapLayerTypeOption = mapLayerTypeOptions.find(
      (o) => o.value === inputMapLayer.mapLayerType,
    );

    if (!isNew) {
      validatorManager.setIsValid(validator.layerName, true);
    }

    setMapLayer(inputMapLayer);
  }, [project, tableColumns, inputMapLayer, isNew, validatorManager]);

  const onLayerNameChanged = (event: any) => {
    const newValue = event.target.value;

    const newMapLayer = { ...mapLayer, layerName: newValue };
    setMapLayer(newMapLayer);

    const isValid = newValue.length !== 0;
    validatorManager.setIsValid(validator.layerName, isValid);
  };

  const onSave = async () => {
    setErrorMessage("");

    if (!validatorManager.validate()) {
      console.log("return");
      return;
    }

    try {
      if (isNew) {
        await onCreateMapLayer(mapLayer);
      } else {
        await onUpdateMapLayer(mapLayer);
      }
    } catch (error: any) {
      const errorMessage = error.response.data;
      setErrorMessage(errorMessage);
      return;
    }
  };

  const onDeleteConfirm = () => {
    setShowDeleteWindow(true);
  };

  const onDeleteOk = async () => {
    setShowDeleteWindow(false);

    onDeleteMapLayer(mapLayer);
  };

  const onDeleteCancel = () => {
    setShowDeleteWindow(false);
  };

  // --------- IsShared

  const onIsSharedChanged = (event: any) => {
    const checked = event.target.checked;

    if (checked) {
      onConfirmIsShared();
      return;
    }

    const newMapLayer = { ...mapLayer, isShared: false };
    setMapLayer(newMapLayer);
  };

  const onConfirmIsShared = () => {
    setShowConfirmIsShared(true);
  };

  const onOkIsShared = () => {
    setShowConfirmIsShared(false);

    const newMapLayer = { ...mapLayer, isShared: true };
    setMapLayer(newMapLayer);
  };

  const onCancelIsShared = () => {
    setShowConfirmIsShared(false);
  };

  /////////////////////

  // --------- IsDefault

  const onIsDefaultChanged = (event: any) => {
    const checked = event.target.checked;

    if (checked) {
      onConfirmIsDefault();
      return;
    }

    const newMapLayer = { ...mapLayer, isDefault: false, isShared: false };
    setMapLayer(newMapLayer);
  };

  const onConfirmIsDefault = () => {
    setShowConfirmIsDefault(true);
  };

  const onOkIsDefault = () => {
    setShowConfirmIsDefault(false);

    const newMapLayer = { ...mapLayer, isDefault: true, isShared: true };
    setMapLayer(newMapLayer);
  };

  const onCancelIsDefault = () => {
    setShowConfirmIsDefault(false);
  };

  /////////////////////

  // --------- Save

  const onConfirmSave = () => {
    if (mapLayer.isShared || mapLayer.isDefault) {
      setShowConfirmSave(true);
    } else {
      onOkSave();
    }
  };

  const onOkSave = async () => {
    setShowConfirmSave(false);

    await onSave();
  };

  const onCancelSave = () => {
    setShowConfirmSave(false);
  };

  /////////////////////

  const onResetToDefault = () => {
    if (!mapLayer.parentMapLayer) {
      return;
    }

    const defaultMapLayer = structuredClone(mapLayer.parentMapLayer);

    if (!defaultMapLayer) {
      return;
    }

    defaultMapLayer.isShared = false;
    defaultMapLayer.isDefault = false;
    defaultMapLayer.isSharedDataChanged = false;

    defaultMapLayer.idMapLayer = mapLayer.idMapLayer;
    defaultMapLayer.idUser = mapLayer.idUser;
    defaultMapLayer.idParentMapLayer = mapLayer.idParentMapLayer;
    defaultMapLayer.parentMapLayer = mapLayer.parentMapLayer;

    defaultMapLayer.selectedMapLayerTypeOption =
      mapLayer.selectedMapLayerTypeOption;

    switch (mapLayer.mapLayerType) {
      case MapLayerType.ThematicLayer:
        if (defaultMapLayer.thematicMapLayer && mapLayer.thematicMapLayer) {
          defaultMapLayer.thematicMapLayer.idThematicMapLayer =
            mapLayer.thematicMapLayer.idThematicMapLayer;
          defaultMapLayer.thematicMapLayer.idMapLayer =
            mapLayer.thematicMapLayer.idMapLayer;

          // mark thematic ranges as new
          for (const range of defaultMapLayer.thematicMapLayer
            .thematicMapLayerRanges) {
            range.idThematicMapLayerRange = 0;
            range.idThematicMapLayer =
              mapLayer.thematicMapLayer.idThematicMapLayer;
          }
        }
        break;

      default:
        break;
    }

    setMapLayer(defaultMapLayer);
  };

  const getSharedOrDefaultLabel = () => {
    if (mapLayer.isDefault) {
      return "Default";
    }

    if (mapLayer.isShared) {
      return "Shared";
    }

    return "";
  };

  const onMapLayerTypeOptionChanged = (
    newValue: DropdownOption<MapLayerType>,
  ) => {
    mapLayer.selectedMapLayerTypeOption = newValue;

    if (!newValue.value) {
      return;
    }

    const newMapLayer = { ...mapLayer };

    newMapLayer.mapLayerType = newValue.value;

    newMapLayer.thematicMapLayer = undefined;
    newMapLayer.geospatialMapLayer = undefined;

    switch (newMapLayer.mapLayerType) {
      case MapLayerType.ThematicLayer:
        newMapLayer.thematicMapLayer = new ThematicMapLayer();
        break;

      case MapLayerType.GeospatialLayer:
        newMapLayer.geospatialMapLayer = new GeospatialMapLayer();
        break;

      default:
        break;
    }

    setMapLayer(newMapLayer);
  };

  return (
    <div style={{ background: "white", padding: "10px", minWidth: "300px" }}>
      <div className="row">
        <div className="mb-3 d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center">
            <div style={{ textWrap: "nowrap" }}>
              <b>
                {isNew
                  ? "New Layer"
                  : `Edit ${getSharedOrDefaultLabel()} Layer`}
              </b>
            </div>
            <MapLayerShareIcon mapLayer={mapLayer} />
            {!mapLayer.parentMapLayer && (
              <div className="d-flex align-items-center ms-2">
                <label className="form-check-label" htmlFor="isShared">
                  Share
                </label>
                <div className="form-switch form-switch-right-text">
                  <input
                    className="form-check-input form-switch-right-text"
                    type="checkbox"
                    checked={mapLayer.isShared}
                    onChange={onIsSharedChanged}
                    id="isShared"
                    disabled={mapLayer.isDefault}
                  />
                </div>
                {showConfirmIsShared && (
                  <ModalWindow
                    title="Share Layer"
                    onOk={onOkIsShared}
                    onCancel={onCancelIsShared}
                  >
                    <div>
                      Are you sure you want to share layer to be visible by all
                      users?
                    </div>
                  </ModalWindow>
                )}
              </div>
            )}
          </div>
          {user?.isPortalAdmin && !mapLayer.parentMapLayer && (
            <div className="d-flex align-items-center ms-3">
              <div className="badge bg-secondary me-2">PORTAL ADMIN</div>
              <label
                className="form-check-label"
                style={{ textWrap: "nowrap" }}
                htmlFor="isDefaultLayer"
              >
                Project Default Layer
              </label>
              <div className="form-switch form-switch-right-text">
                <input
                  className="form-check-input form-switch-right-text"
                  type="checkbox"
                  checked={mapLayer.isDefault}
                  onChange={onIsDefaultChanged}
                  id="isDefaultLayer"
                />
              </div>
              {showConfirmIsDefault && (
                <ModalWindow
                  title="Set Project Default Map Layer"
                  onOk={onOkIsDefault}
                  onCancel={onCancelIsDefault}
                >
                  <div>
                    Are you sure you want to set the map layer as a project
                    default map layer to be visible by all users?
                  </div>
                </ModalWindow>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="row">
        <div className="d-flex mb-3">
          <div className="flex-grow-1">
            <label htmlFor="layerName" className="form-label">
              Layer Name
            </label>
            <div className="input-group" style={{ width: "auto" }}>
              <Validator
                name={validator.layerName}
                errorMessage="Please enter a layer name."
                validationManager={validatorManager}
              >
                <input
                  type="text"
                  className="form-control"
                  id="layerName"
                  value={mapLayer.layerName}
                  onChange={onLayerNameChanged}
                />
              </Validator>
            </div>
          </div>
          <div className="ms-3" style={{ zIndex: 3 }}>
            <label htmlFor="layerName" className="form-label">
              Layer Type
            </label>
            <Dropdown
              minWidth="80px"
              height="35px"
              options={mapLayerTypeOptions}
              value={mapLayer.selectedMapLayerTypeOption}
              onChange={(newValue: any) => {
                onMapLayerTypeOptionChanged(newValue);
              }}
              disabled={!isNew}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="mb-3">
          {mapLayer.mapLayerType === MapLayerType.ThematicLayer && (
            <ThematicMapLayerEdit
              project={project}
              tableColumns={tableColumns}
              mapLayer={mapLayer}
              onSetMapLayer={(newMapLayer: MapLayer) =>
                setMapLayer(newMapLayer)
              }
            />
          )}
          {mapLayer.mapLayerType === MapLayerType.GeospatialLayer && (
            <GeospatialMapLayerEdit
              mapLayer={mapLayer}
              onSetMapLayer={(newMapLayer: MapLayer) =>
                setMapLayer(newMapLayer)
              }
              isNew={isNew}
              validatorManager={validatorManager}
            />
          )}
        </div>
      </div>
      <div className="d-flex justify-content-between">
        <div>
          <button className="btn btn-sm btn-primary" onClick={onConfirmSave}>
            {isNew ? "Create" : "Save"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            style={{ marginLeft: "10px" }}
            onClick={() => {
              onCancel();
            }}
          >
            Cancel
          </button>
          {errorMessage !== "" && (
            <div className="error-message col-md-12 text-center mt-3">
              {errorMessage}
            </div>
          )}
        </div>
        {!isNew && !mapLayer.parentMapLayer && (
          <div>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={onDeleteConfirm}
            >
              Delete
            </button>
          </div>
        )}
        {mapLayer.parentMapLayer && (
          <div>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={onResetToDefault}
            >
              Reset to Default
            </button>
          </div>
        )}
      </div>
      {showDeleteWindow && (
        <ModalWindow title="Delete" onOk={onDeleteOk} onCancel={onDeleteCancel}>
          <div>
            {`Are you sure you want to delete the map layer: ${mapLayer?.layerName}?`}
          </div>
        </ModalWindow>
      )}
      {showConfirmSave && (
        <ModalWindow
          title={isNew ? "Create" : "Save"}
          onOk={onOkSave}
          onCancel={onCancelSave}
        >
          <div>
            You are {isNew ? "creating" : "editing"} a{" "}
            <span className="text-danger">
              <b>{`${getSharedOrDefaultLabel()} Layer`}</b>
            </span>
            .{" "}
            <b>
              {isNew ? "The layer" : "Changes"} will be visible to all users.
            </b>
            <br />
            Are you sure you want to{" "}
            {isNew ? "create the layer" : "save changes"}?
          </div>
        </ModalWindow>
      )}
    </div>
  );
};

export default MapLayerEdit;
